import type { useIntl } from 'react-intl';

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

interface WelcomeContentParams {
  formatMessage: FormatMessage;
  isAccountant: boolean;
  isEmbedded: boolean;
  isExistingCompany: boolean;
  isInvoicing: boolean;
  isLegalPlaceFastTrack: boolean;
  organizationName?: string;
  ownerFirstName?: string;
  /** Co-branding display name, set only for co-branded partners (Dougs). */
  partnerDisplayName?: string;
  partnerName?: string;
}

export function getWelcomeContent(params: WelcomeContentParams): {
  ctaLabel: string;
  featuresList: string[];
  subtitle: string;
  title: string;
} {
  return {
    ctaLabel: getCtaLabel(params),
    featuresList: getFeaturesList(params),
    subtitle: getSubtitle(params),
    title: getTitle(params),
  };
}

function getTitle({
  formatMessage,
  isInvoicing,
  ownerFirstName,
  partnerDisplayName,
}: WelcomeContentParams): string {
  if (isInvoicing) {
    return formatMessage(
      { id: 'customer-registration-kit.partner-redirection.qonto-invoicing.title' },
      { owner_first_name: ownerFirstName }
    );
  }
  if (partnerDisplayName) {
    return formatMessage(
      { id: 'customer-registration-kit.partner-redirection.welcome.partner.title' },
      { name: ownerFirstName, partner: partnerDisplayName }
    );
  }
  return formatMessage(
    { id: 'customer-registration-kit.partner-redirection.welcome.title' },
    { owner_first_name: ownerFirstName }
  );
}

function getSubtitle({
  formatMessage,
  isEmbedded,
  isExistingCompany,
  isInvoicing,
  isLegalPlaceFastTrack,
  organizationName,
  partnerDisplayName,
}: WelcomeContentParams): string {
  if (isInvoicing) {
    return formatMessage(
      { id: 'customer-registration-kit.partner-redirection.qonto-invoicing.subtitle' },
      { organization_name: organizationName }
    );
  }
  if (partnerDisplayName && isEmbedded) {
    return formatMessage(
      { id: 'customer-registration-kit.partner-redirection.embedded.subtitle.partner-name' },
      { partner: partnerDisplayName }
    );
  }
  if (partnerDisplayName) {
    return formatMessage(
      { id: 'customer-registration-kit.partner-redirection.welcome.partner.subtitle' },
      { company_name: organizationName, partner: partnerDisplayName }
    );
  }
  if (isEmbedded) {
    return formatMessage({ id: 'customer-registration-kit.partner-redirection.embedded.subtitle' });
  }
  if (isLegalPlaceFastTrack) {
    return formatMessage(
      { id: 'customer-registration-kit.partner-redirection.welcome.legalplace.subtitle' },
      { company_name: organizationName }
    );
  }
  if (isExistingCompany) {
    return formatMessage(
      { id: 'customer-registration-kit.partner-redirection.existing-company.subtitle' },
      { organization_name: organizationName }
    );
  }
  return formatMessage(
    { id: 'customer-registration-kit.partner-redirection.welcome.subtitle' },
    { organization_name: organizationName }
  );
}

function getFeaturesList({
  formatMessage,
  isAccountant,
  isEmbedded,
  isInvoicing,
  isLegalPlaceFastTrack,
  partnerDisplayName,
  partnerName,
}: WelcomeContentParams): string[] {
  if (isInvoicing) {
    return [
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.qonto-invoicing.features.e-invoicing',
      }),
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.qonto-invoicing.features.invoice-sync',
      }),
    ];
  }
  if (partnerDisplayName && isEmbedded) {
    return [
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.embedded.list.item.1.qonto',
      }),
      formatMessage(
        { id: 'customer-registration-kit.partner-redirection.embedded.list.item.2' },
        { partner: partnerDisplayName }
      ),
      formatMessage({ id: 'customer-registration-kit.partner-redirection.embedded.list.item.3' }),
    ];
  }
  if (isEmbedded) {
    return [
      formatMessage(
        { id: 'customer-registration-kit.partner-redirection.embedded.list.item.1' },
        { partner: partnerName }
      ),
      formatMessage(
        { id: 'customer-registration-kit.partner-redirection.embedded.list.item.2' },
        { partner: partnerName }
      ),
      formatMessage({ id: 'customer-registration-kit.partner-redirection.embedded.list.item.3' }),
    ];
  }
  if (isLegalPlaceFastTrack) {
    return [
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.welcome.legalplace.list.item.1',
      }),
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.welcome.legalplace.list.item.2',
      }),
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.welcome.legalplace.list.item.3',
      }),
    ];
  }
  if (isAccountant) {
    return [
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.welcome.accountant.list.item.1',
      }),
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.welcome.accountant.list.item.2',
      }),
      formatMessage({
        id: 'customer-registration-kit.partner-redirection.welcome.accountant.list.item.3',
      }),
    ];
  }
  return [
    formatMessage({ id: 'customer-registration-kit.partner-redirection.welcome.list.item.1' }),
    formatMessage({ id: 'customer-registration-kit.partner-redirection.welcome.list.item.2' }),
    formatMessage({ id: 'customer-registration-kit.partner-redirection.welcome.list.item.3' }),
  ];
}

function getCtaLabel({ formatMessage, isExistingCompany }: WelcomeContentParams): string {
  return formatMessage({
    id: isExistingCompany
      ? 'customer-registration-kit.partner-redirection.existing-company.cta'
      : 'customer-registration-kit.partner-redirection.welcome.cta',
  });
}
